<template>
  <div class="payment-test">
    <div class="v3-header">UPI Deep Link Test</div>

    <div class="page-pad">
      <div class="intro-bar">
        Tap a wallet to pay ₹1.00. Come back and submit the result.
      </div>

      <!-- One quick-pay grid per payee -->
      <div v-for="group in schemeGroups" :key="group.id" class="section-card">
        <div class="section-header">
          <van-icon name="apps-o" color="#5f259f" size="18" />
          <span>Pay {{ group.title }}</span>
        </div>
        <div class="section-body">
          <div class="payee-line">₹1.00 → {{ group.vpa }}</div>
          <div class="payment-box-parent">
            <div
              v-for="wallet in wallets"
              :key="group.id + '-' + wallet.id"
              class="payment-box"
              @click="onPayClick(group, wallet, 'wallet_scheme', 'current')"
            >
              <img v-if="wallet.icon" class="image-sm" :src="wallet.icon" :alt="wallet.name">
              <div v-else class="wallet-badge" :style="{ background: wallet.color }">{{ wallet.short }}</div>
              <div class="text-container">
                <span class="text">{{ wallet.name }}</span>
                <span v-if="isDone(group, wallet.id, 'wallet_scheme', 'current')" class="done-hint">Done</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- BharatPe param test: same Paytm / PhonePe buttons, one pair per param set -->
      <div class="section-card">
        <div class="section-header">
          <van-icon name="apps-o" color="#5f259f" size="18" />
          <span>BharatPe merchant — try each param set</span>
        </div>
        <div class="section-body">
          <div class="payee-line">₹1.00 → {{ bharatpePayee.vpa }}</div>
          <div v-for="variant in paramVariants" :key="'bp-' + variant.id" class="variant-block">
            <div class="variant-title">{{ variant.label }}</div>
            <div class="payment-box-parent">
              <div
                v-for="wallet in bharatpeWallets"
                :key="'bp-' + variant.id + '-' + wallet.id"
                class="payment-box"
                @click="onPayClick(bharatpePayee, wallet, 'bharatpe_param', variant.id)"
              >
                <img v-if="wallet.icon" class="image-sm" :src="wallet.icon" :alt="wallet.name">
                <div v-else class="wallet-badge" :style="{ background: wallet.color }">{{ wallet.short }}</div>
                <div class="text-container">
                  <span class="text">{{ wallet.name }}</span>
                  <span v-if="isDone(bharatpePayee, wallet.id, 'bharatpe_param', variant.id)" class="done-hint">Done</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="section-card">
        <div class="section-header">
          <van-icon name="orders-o" color="#5f259f" size="18" />
          <span>Test Results (this session)</span>
        </div>
        <div class="section-body">
          <div v-if="!sessionResults.length" class="empty-tip">No tests yet. Tap a wallet to start.</div>
          <div v-for="row in sessionResults" :key="row.testNo" class="result-row">
            <div class="result-main">
              <strong>{{ row.walletName }}</strong>
              <span class="result-meta">{{ row.sceneLabel }} · {{ row.variantLabel }}</span>
              <span class="result-meta">{{ row.payeeVpa }}</span>
              <span class="result-answer">{{ row.summary }}</span>
            </div>
            <button v-if="row.reportFailed" class="retry-btn" @click="retryReport(row)">Retry</button>
            <span v-else class="ok-tag">{{ row.surveyed ? 'Saved' : 'Pending' }}</span>
          </div>
        </div>
      </div>
    </div>

    <van-popup v-model="surveyVisible" round :close-on-click-overlay="false" :get-container="'body'">
      <div class="survey-dialog" v-if="activeSurvey">
        <div class="survey-title">Test Result — {{ activeSurvey.walletName }}</div>
        <div class="survey-sub">
          {{ activeSurvey.payeeVpa }} · ₹{{ amount }}
          <span v-if="activeSurvey.paramVariant !== 'current'"> · {{ variantLabel(activeSurvey.paramVariant) }}</span>
        </div>

        <div class="q-block">
          <div class="q-text">Q1. Did the correct payment page open (payee and amount pre-filled)?</div>
          <div class="yesno">
            <button :class="['yn-btn', { on: activeSurvey.openedPayPage === 1 }]" @click="answerOpened(1)">Yes</button>
            <button :class="['yn-btn', { on: activeSurvey.openedPayPage === 0 }]" @click="answerOpened(0)">No</button>
          </div>
        </div>

        <div v-if="activeSurvey.openedPayPage === 0" class="q-block">
          <div class="q-text">Q2. Was {{ activeSurvey.walletName }} launched?</div>
          <div class="yesno">
            <button :class="['yn-btn', { on: activeSurvey.walletLaunched === 1 }]" @click="answerLaunched(1)">Yes</button>
            <button :class="['yn-btn', { on: activeSurvey.walletLaunched === 0 }]" @click="answerLaunched(0)">No</button>
          </div>
        </div>

        <div v-if="activeSurvey.openedPayPage === 0 && activeSurvey.walletLaunched === 0" class="q-block">
          <div class="q-text">Q3. Is {{ activeSurvey.walletName }} installed on this phone?</div>
          <div class="yesno">
            <button :class="['yn-btn', { on: activeSurvey.walletInstalled === 1 }]" @click="activeSurvey.walletInstalled = 1">Yes</button>
            <button :class="['yn-btn', { on: activeSurvey.walletInstalled === 0 }]" @click="activeSurvey.walletInstalled = 0">No</button>
          </div>
        </div>

        <div v-if="resultCodeRequired" class="q-block">
          <div class="q-text">What happened? (required)</div>
          <div class="result-opts">
            <button
              v-for="opt in resultCodeOptions"
              :key="opt.id"
              :class="['opt-btn', { on: activeSurvey.resultCode === opt.id }]"
              @click="activeSurvey.resultCode = opt.id"
            >{{ opt.label }}</button>
          </div>
        </div>

        <div class="q-block">
          <div class="q-text">Exact error message shown by the app (optional, but please copy it)</div>
          <van-field
            v-model="activeSurvey.errorText"
            type="textarea"
            rows="2"
            placeholder="Copy the error text from the wallet app"
            style="border: 1px solid #eee; border-radius: 0.06rem;"
          />
        </div>

        <div class="survey-actions">
          <button class="dialog-btn" @click="skipSurvey">Skip</button>
          <button class="dialog-btn primary" :disabled="!canSubmitSurvey" @click="submitSurvey">Submit</button>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import paytmIcon from "@/assets/img/paytm_simple.png";
import phonepeIcon from "@/assets/img/phonepe_simple.png";
import mobikwikIcon from "@/assets/img/h5_mobikwik.png";

const PENDING_KEY = "pv3_deeplink_pending_survey";
const RESULTS_KEY = "pv3_deeplink_session_results";
const UUID_KEY = "pv3_visitor_uuid";

export default {
  name: "paymentTest",
  data() {
    return {
      tester: "",
      visitorUuid: "",
      payeeName: "PAYMENT",
      amount: "1.00",
      mc: "5411",
      mode: "04",
      purpose: "00",
      surveyVisible: false,
      activeSurvey: null,
      sessionResults: [],
      surveyTimer: null,
      schemeGroups: [
        { id: "personal", type: "personal", title: "personal UPI", vpa: "9079284665@mbkns" },
        { id: "bharatpe-unitype", type: "bharatpe", title: "BharatPe @unitype", vpa: "BHARATPE.9W0D0I1J3L934515@unitype" },
        { id: "bharatpe-fbpe", type: "bharatpe", title: "BharatPe @fbpe", vpa: "BHARATPE.8O0H0F6S2A55869@fbpe" },
        { id: "bharatpe-yes", type: "bharatpe", title: "BharatPe @yesbankltd", vpa: "BHARATPE09B9Z1I8I2T12123@yesbankltd" },
      ],
      wallets: [
        { id: "paytm", name: "Paytm", short: "PT", color: "#00baf2", icon: paytmIcon },
        { id: "phonepe", name: "PhonePe", short: "PP", color: "#5f259f", icon: phonepeIcon },
        { id: "bhim", name: "BHIM", short: "BH", color: "#f57c00", icon: "" },
        { id: "amzn", name: "Amazon Pay", short: "AZ", color: "#ff9900", icon: "" },
        { id: "cred", name: "CRED", short: "CR", color: "#1a1a1a", icon: "" },
        { id: "whatsapp", name: "WhatsApp", short: "WA", color: "#25d366", icon: "" },
        { id: "mobikwik", name: "MobiKwik", short: "MK", color: "#e21b22", icon: mobikwikIcon },
        { id: "gpay", name: "Google Pay", short: "GP", color: "#4285f4", icon: "" },
        { id: "gpay_upi", name: "Google Pay (upi)", short: "GU", color: "#34a853", icon: "" },
      ],
      paramVariants: [
        { id: "current", label: "Same as live checkout" },
        { id: "with_tr", label: "Add transaction id" },
        { id: "p2m_full", label: "Merchant fields (unsigned)" },
        { id: "minimal", label: "Amount + UPI only" },
      ],
      resultCodeOptions: [
        { id: "ok", label: "Payment page opened, ready to pay" },
        { id: "app_home", label: "App opened but stayed on home screen" },
        { id: "payee_invalid", label: "Error: invalid payee / VPA not found" },
        { id: "merchant_not_registered", label: "Error: merchant not registered / not supported" },
        { id: "limit_exceeded", label: "Error: limit exceeded" },
        { id: "not_launched", label: "App did not open at all" },
        { id: "other", label: "Something else" },
      ],
    };
  },
  computed: {
    bharatpePayee() {
      return this.schemeGroups.find((g) => g.id === "bharatpe-unitype") || this.schemeGroups[1];
    },
    bharatpeWallets() {
      return this.wallets.filter((w) => w.id === "paytm" || w.id === "phonepe");
    },
    resultCodeRequired() {
      return this.activeSurvey && this.activeSurvey.scene === "bharatpe_param";
    },
    canSubmitSurvey() {
      if (!this.activeSurvey) return false;
      if (this.activeSurvey.openedPayPage !== 0 && this.activeSurvey.openedPayPage !== 1) return false;
      if (this.activeSurvey.openedPayPage === 0 && this.activeSurvey.walletLaunched !== 0 && this.activeSurvey.walletLaunched !== 1) {
        return false;
      }
      if (this.activeSurvey.openedPayPage === 0 && this.activeSurvey.walletLaunched === 0
          && this.activeSurvey.walletInstalled !== 0 && this.activeSurvey.walletInstalled !== 1) {
        return false;
      }
      if (this.resultCodeRequired && !this.activeSurvey.resultCode) return false;
      return true;
    },
  },
  created() {
    this.util.title("UPI Deep Link Test");
    this.visitorUuid = this.buildVisitorUuid();
    this.tester = this.readTesterFromRoute();
    this.sessionResults = this.readJson(RESULTS_KEY) || [];
    this.restorePendingSurvey();
    console.log("[paymentTest] 测试页初始化完成, tester=" + this.tester + ", visitorUuid=" + this.visitorUuid);
  },
  mounted() {
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    window.addEventListener("pageshow", this.handlePageShow);
  },
  beforeDestroy() {
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    window.removeEventListener("pageshow", this.handlePageShow);
    clearTimeout(this.surveyTimer);
    this.surveyTimer = null;
  },
  methods: {
    /**
     * 测试者标识只从路由读取：/payment-test?tester=alice
     */
    readTesterFromRoute() {
      const raw = this.$route && this.$route.query ? this.$route.query.tester : "";
      const tester = String(raw || "").trim().slice(0, 64);
      console.log("[paymentTest] 从路由读取测试者, tester=" + tester);
      return tester;
    },
    variantLabel(id) {
      const found = this.paramVariants.find((v) => v.id === id);
      return found ? found.label : id;
    },
    isDone(payee, walletId, scene, variant) {
      return this.sessionResults.some((row) =>
        row.surveyed
        && row.payeeVpa === payee.vpa
        && row.wallet === walletId
        && row.scene === scene
        && row.paramVariant === variant
      );
    },
    buildVisitorUuid() {
      try {
        const ls = window.localStorage;
        if (ls) {
          const fromHost = ls.getItem("deviceid");
          if (fromHost) return fromHost;
          const cached = ls.getItem(UUID_KEY);
          if (cached) return cached;
          const gen = (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function")
            ? crypto.randomUUID()
            : "pv3-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
          try { ls.setItem(UUID_KEY, gen); } catch (e) {}
          return gen;
        }
      } catch (e) {}
      return "pv3-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
    },
    buildUpiParams(payeeVpa, variant) {
      const params = new URLSearchParams();
      params.set("pa", (payeeVpa || "").trim());
      params.set("pn", this.payeeName);
      params.set("cu", "INR");
      params.set("am", this.amount);
      if (variant === "minimal") {
        return params;
      }
      if (variant === "current") {
        params.set("tn", "00004");
        return params;
      }
      const tr = ("PTEST" + Date.now()).replace(/[^a-zA-Z0-9]/g, "").slice(0, 35);
      params.set("tr", tr);
      params.set("tn", ("Pay " + tr).slice(0, 80));
      if (variant === "p2m_full") {
        params.set("mc", this.mc);
        params.set("mode", this.mode);
        params.set("purpose", this.purpose);
      }
      return params;
    },
    buildDeepLink(walletId, payeeVpa, variant) {
      const query = this.buildUpiParams(payeeVpa, variant).toString();
      if (walletId === "paytm") {
        return "paytmmp://cash_wallet?" + query + "&featuretype=money_transfer";
      }
      if (walletId === "phonepe") {
        return this.buildPhonePeDeepLink(payeeVpa, variant);
      }
      if (walletId === "gpay_upi") {
        return "gpay://upi/pay?" + query;
      }
      const schemeMap = {
        bhim: "bhim://pay?",
        amzn: "amzn://pay?",
        cred: "cred://pay?",
        whatsapp: "whatsapp://pay?",
        mobikwik: "mobikwik://pay?",
        gpay: "gpay://pay?",
      };
      return (schemeMap[walletId] || "upi://pay?") + query;
    },
    buildPhonePeDeepLink(payeeVpa, variant) {
      const q = this.buildUpiParams(payeeVpa, variant);
      const pa = q.get("pa") || "";
      const am = q.get("am") || "0";
      const tn = q.get("tn") || "";
      const paiseAmount = Number(Number(am).toFixed(2).replace(".", ""));
      const payload = {
        contact: { cbsName: "", nickName: "", type: "VPA", vpa: pa },
        p2pPaymentCheckoutParams: {
          checkoutType: "DEFAULT",
          initialAmount: paiseAmount,
          note: tn,
          isByDefaultKnownContact: true,
          disableViewHistory: true,
          shouldShowMaskedNumber: true,
          shouldShowUnsavedContactBanner: false,
          showKeyboard: true,
          allowAmountEdit: false,
          disableNotesEdit: true,
          currency: "INR",
          showQrCodeOption: false,
          enableSpeechToText: false,
          transactionContext: "p2p",
          isRecurring: false,
        },
      };
      const data = window.btoa(unescape(encodeURIComponent(JSON.stringify(payload))));
      return "phonepe://native?data=" + data + "&id=p2ppayment";
    },
    onPayClick(payee, wallet, scene, variant) {
      const vpa = (payee.vpa || "").trim();
      if (!vpa) return;
      const testNo = "DLT-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 8);
      const deepLink = this.buildDeepLink(wallet.id, vpa, variant);
      const record = {
        testNo,
        event: "click",
        tester: this.tester || "",
        visitorUuid: this.visitorUuid,
        scene,
        wallet: wallet.id,
        walletName: wallet.name,
        payeeType: payee.type,
        payeeVpa: vpa,
        amount: this.amount,
        paramVariant: variant,
        deepLink,
        openedPayPage: null,
        walletLaunched: null,
        walletInstalled: null,
        resultCode: "",
        errorText: "",
        surveyed: false,
        reportFailed: false,
        leftPage: false,
        sceneLabel: scene === "bharatpe_param" ? "BharatPe params" : "Wallet scheme",
        variantLabel: this.variantLabel(variant),
        summary: "Waiting for survey",
      };
      console.log("[paymentTest] 点击快捷支付, wallet=" + wallet.id + ", payee=" + vpa + ", scene=" + scene + ", variant=" + variant);
      this.upsertSession(record);
      this.writeLs(PENDING_KEY, JSON.stringify(record));
      this.reportPayload(record);
      this.activeSurvey = Object.assign({}, record);
      this.scheduleSurveyFallback();
      try {
        window.location.href = deepLink;
      } catch (e) {
        console.warn("[paymentTest] 打开深链失败", e);
        this.openSurvey();
      }
    },
    scheduleSurveyFallback() {
      clearTimeout(this.surveyTimer);
      this.surveyTimer = setTimeout(() => {
        this.surveyTimer = null;
        if (!this.surveyVisible && this.readJson(PENDING_KEY)) {
          console.log("[paymentTest] 2.5s 未离开页面，直接弹出问卷");
          this.openSurvey();
        }
      }, 2500);
    },
    handleVisibilityChange() {
      if (document.hidden) {
        if (this.activeSurvey) this.activeSurvey.leftPage = true;
        return;
      }
      this.openSurvey();
    },
    handlePageShow() {
      this.openSurvey();
    },
    restorePendingSurvey() {
      const pending = this.readJson(PENDING_KEY);
      if (!pending || !pending.testNo) return;
      this.activeSurvey = pending;
      this.surveyVisible = true;
      console.log("[paymentTest] 恢复未完成问卷, testNo=" + pending.testNo);
    },
    openSurvey() {
      const pending = this.activeSurvey || this.readJson(PENDING_KEY);
      if (!pending || pending.surveyed) return;
      this.activeSurvey = pending;
      this.surveyVisible = true;
    },
    answerOpened(val) {
      this.activeSurvey.openedPayPage = val;
      if (val === 1) {
        this.activeSurvey.walletLaunched = null;
        this.activeSurvey.walletInstalled = null;
      }
    },
    answerLaunched(val) {
      this.activeSurvey.walletLaunched = val;
      if (val === 1) {
        this.activeSurvey.walletInstalled = null;
      }
    },
    skipSurvey() {
      console.log("[paymentTest] 用户跳过问卷, testNo=" + (this.activeSurvey && this.activeSurvey.testNo));
      this.clearPending();
      this.surveyVisible = false;
      this.activeSurvey = null;
    },
    submitSurvey() {
      if (!this.canSubmitSurvey) return;
      const payload = Object.assign({}, this.activeSurvey, {
        event: "survey",
        surveyed: true,
        summary: this.buildSummary(this.activeSurvey),
      });
      console.log("[paymentTest] 提交问卷, testNo=" + payload.testNo + ", opened=" + payload.openedPayPage);
      this.upsertSession(payload);
      this.reportPayload(payload, (ok) => {
        payload.reportFailed = !ok;
        this.upsertSession(payload);
      });
      this.clearPending();
      this.surveyVisible = false;
      this.activeSurvey = null;
    },
    buildSummary(row) {
      if (row.openedPayPage === 1) return "Q1 Yes · payment page OK";
      const parts = ["Q1 No"];
      if (row.walletLaunched === 1) parts.push("Q2 launched");
      if (row.walletLaunched === 0) parts.push("Q2 not launched");
      if (row.walletInstalled === 1) parts.push("Q3 installed");
      if (row.walletInstalled === 0) parts.push("Q3 not installed");
      if (row.resultCode) parts.push(row.resultCode);
      return parts.join(" · ");
    },
    retryReport(row) {
      this.reportPayload(row, (ok) => {
        row.reportFailed = !ok;
        this.upsertSession(row);
      });
    },
    reportPayload(row, done) {
      const fd = new FormData();
      fd.append("event", row.event || "click");
      fd.append("testNo", row.testNo || "");
      fd.append("tester", row.tester || this.tester || "");
      fd.append("uuid", this.visitorUuid || "");
      fd.append("scene", row.scene || "");
      fd.append("wallet", row.wallet || "");
      fd.append("payeeType", row.payeeType || "");
      fd.append("payeeVpa", row.payeeVpa || "");
      fd.append("amount", row.amount || this.amount);
      if (row.paramVariant) fd.append("paramVariant", row.paramVariant);
      if (row.deepLink) fd.append("deepLink", row.deepLink);
      if (row.openedPayPage === 0 || row.openedPayPage === 1) fd.append("openedPayPage", String(row.openedPayPage));
      if (row.walletLaunched === 0 || row.walletLaunched === 1) fd.append("walletLaunched", String(row.walletLaunched));
      if (row.walletInstalled === 0 || row.walletInstalled === 1) fd.append("walletInstalled", String(row.walletInstalled));
      if (row.resultCode) fd.append("resultCode", row.resultCode);
      if (row.errorText) fd.append("errorText", row.errorText);
      const url = "/app/v1/order/payment/trackDeeplinkTest";
      const finish = (ok) => {
        if (typeof done === "function") done(ok);
      };
      try {
        if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
          if (navigator.sendBeacon(url, fd)) {
            finish(true);
            return;
          }
        }
      } catch (e) {}
      if (typeof fetch === "function") {
        fetch(url, { method: "POST", body: fd, keepalive: true, credentials: "include" })
          .then(() => finish(true))
          .catch(() => finish(false));
        return;
      }
      finish(false);
    },
    upsertSession(row) {
      const list = this.sessionResults.slice();
      const idx = list.findIndex((x) => x.testNo === row.testNo);
      if (idx >= 0) {
        list.splice(idx, 1, Object.assign({}, list[idx], row));
      } else {
        list.unshift(row);
      }
      this.sessionResults = list.slice(0, 40);
      this.writeLs(RESULTS_KEY, JSON.stringify(this.sessionResults));
    },
    clearPending() {
      try { window.localStorage.removeItem(PENDING_KEY); } catch (e) {}
    },
    readLs(key) {
      try { return window.localStorage.getItem(key); } catch (e) { return null; }
    },
    writeLs(key, value) {
      try { window.localStorage.setItem(key, value); } catch (e) {}
    },
    readJson(key) {
      const raw = this.readLs(key);
      if (!raw) return null;
      try { return JSON.parse(raw); } catch (e) { return null; }
    },
  },
};
</script>

<style scoped>
.payment-test {
  min-height: 100vh;
  background-color: #fff;
  box-sizing: border-box;
  font-family: Avenir, Helvetica, Arial, sans-serif;
}
.v3-header {
  padding: 0 0.12rem;
  height: 0.4rem;
  color: #fff;
  background-color: #5f259f;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.14rem;
  font-weight: 700;
}
.page-pad {
  padding: 0.08rem;
}
.intro-bar {
  margin-top: 0.08rem;
  padding: 0.1rem 0.12rem;
  background: #fff7ed;
  border: 1px solid #ffe0b2;
  border-radius: 0.08rem;
  color: #eb7824;
  font-size: 0.13rem;
  font-weight: 600;
}
.section-card {
  background: #fff;
  border-radius: 0.1rem;
  border: 1px solid #eee;
  margin-top: 0.08rem;
  overflow: hidden;
}
.section-header {
  display: flex;
  align-items: center;
  gap: 0.08rem;
  padding: 0.08rem 0.12rem;
  background: linear-gradient(135deg, #f8f4ff, #f0eaff);
  border-bottom: 1px solid #eee;
  font-size: 0.14rem;
  font-weight: 700;
  color: #333;
}
.section-body {
  padding: 0.1rem 0.12rem;
}
.payee-line {
  font-size: 0.12rem;
  color: #666;
  margin-bottom: 0.08rem;
  word-break: break-all;
}
.payment-box-parent {
  display: flex;
  flex-wrap: wrap;
  gap: 0.1rem;
}
.payment-box {
  display: flex;
  align-items: center;
  background-color: #fff;
  border-radius: 0.08rem;
  padding: 0.04rem;
  border: 1px solid silver;
  width: calc(50% - 0.05rem);
  margin-bottom: 0.06rem;
  cursor: pointer;
  box-sizing: border-box;
}
.image-sm {
  height: 0.4rem;
  width: 0.4rem;
  object-fit: contain;
}
.wallet-badge {
  width: 0.4rem;
  height: 0.4rem;
  border-radius: 0.08rem;
  color: #fff;
  font-size: 0.12rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.payment-box .text-container {
  margin-left: 0.06rem;
  min-width: 0;
}
.payment-box .text {
  display: block;
  font-weight: 700;
  font-size: 0.13rem;
}
.done-hint {
  display: block;
  font-size: 0.11rem;
  color: #52c41a;
  font-weight: 700;
}
.variant-block {
  margin-top: 0.1rem;
}
.variant-title {
  font-weight: 700;
  font-size: 0.13rem;
  margin-bottom: 0.06rem;
  color: #333;
}
.empty-tip {
  color: #999;
  font-size: 0.13rem;
}
.result-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.08rem;
  padding: 0.08rem 0;
  border-bottom: 1px solid #f2f2f2;
}
.result-main {
  display: flex;
  flex-direction: column;
  gap: 0.02rem;
  font-size: 0.12rem;
}
.result-meta {
  color: #888;
}
.result-answer {
  color: #333;
}
.retry-btn,
.ok-tag {
  font-size: 0.12rem;
  flex-shrink: 0;
}
.retry-btn {
  border: 1px solid #ff4d4f;
  color: #ff4d4f;
  background: #fff2f0;
  border-radius: 999px;
  padding: 0.04rem 0.1rem;
}
.ok-tag {
  color: #52c41a;
}
.survey-dialog {
  width: 3.2rem;
  padding: 0.18rem 0.16rem 0.14rem;
  box-sizing: border-box;
}
.survey-title {
  font-size: 0.16rem;
  font-weight: 700;
  color: #1d2d5a;
}
.survey-sub {
  margin-top: 0.04rem;
  color: #6b7693;
  font-size: 0.12rem;
  word-break: break-all;
}
.q-block {
  margin-top: 0.12rem;
}
.q-text {
  font-size: 0.13rem;
  color: #333;
  margin-bottom: 0.06rem;
}
.yesno {
  display: flex;
  gap: 0.08rem;
}
.yn-btn {
  flex: 1;
  height: 0.36rem;
  border-radius: 999px;
  border: 1px solid #d9c7f0;
  background: #f8f4ff;
  color: #5f259f;
  font-weight: 700;
}
.yn-btn.on {
  background: #5f259f;
  color: #fff;
}
.result-opts {
  display: flex;
  flex-direction: column;
  gap: 0.06rem;
}
.opt-btn {
  text-align: left;
  border: 1px solid #d9c7f0;
  background: #f8f4ff;
  color: #5f259f;
  border-radius: 0.06rem;
  padding: 0.06rem 0.08rem;
  font-size: 0.12rem;
}
.opt-btn.on {
  background: #5f259f;
  color: #fff;
  border-color: #5f259f;
}
.survey-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.12rem;
  margin-top: 0.16rem;
}
.dialog-btn {
  min-width: 0.72rem;
  height: 0.36rem;
  border-radius: 999px;
  background: #f3f4f5;
  color: #66728f;
  font-size: 0.13rem;
  font-weight: 700;
  border: 0;
}
.dialog-btn.primary {
  background: #5f259f;
  color: #fff;
}
.dialog-btn[disabled] {
  opacity: 0.45;
}
</style>
